/**
 * Currently aliasing react-popper to react-popper-2 to fix this issue described here
 * https://github.com/storybookjs/storybook/issues/10982
 */
import React, { ReactElement, useState } from 'react';
import { usePopper } from 'react-popper';
import * as PopperJS from '@popperjs/core';

import PopOverTrigger from '../PopOverTrigger';
import PopOverContent from '../PopOverContent';

export type TUsePopOverProps = {
  children: ReactElement<HTMLDivElement>[];
  /** Displays the popover when hover the trigger. */
  openOnHover?: boolean;
  /** Displays the popover when clicking the trigger. */
  openOnClick?: boolean;
  /** Displays the popover immediately after render. */
  openOnLoad?: boolean;
  /** Defaults to `PLACEMENT.RIGHT`. Possible values are `PLACEMENT.RIGHT` `PLACEMENT.LEFT` `PLACEMENT.TOP`
   * `PLACEMENT.BOTTOM`. */
  placement?: PopperJS.Placement;
  /** The popover is offset 20px along the reference. */
  skidding?: number;
  /** The popover is offset 20px away from the reference. */
  distance?: number;
};

const usePopOver = ({
  children,
  placement,
  skidding,
  distance,
  openOnClick,
  openOnHover,
  openOnLoad = false,
}: TUsePopOverProps) => {
  const TargetTrigger = children.find(({ type }) => type === PopOverTrigger) as ReactElement;
  const TargetContent = children.find(({ type }) => type === PopOverContent) as ReactElement;
  const [isOpen, setIsOpen] = useState(openOnLoad);
  const [referenceElement, setReferenceElement] = useState<any>(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: 'absolute',
    placement,
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [skidding, distance],
        },
      },
    ],
  });
  const triggerProps = {
    placement,
    onClick: (event) => {
      if (!openOnClick) {
        return;
      }

      setIsOpen(!isOpen);

      if (typeof TargetContent?.props?.onClick === 'function') {
        TargetContent.props.onClick(event);
      }
    },
    onBlur: (event) => {
      if (!openOnClick) {
        return;
      }

      setIsOpen(false);

      if (typeof TargetContent?.props?.onClose === 'function') {
        TargetContent.props.onClose(event);
      }
    },
    onMouseOver: () => {
      if (!openOnHover) {
        return;
      }

      setIsOpen(true);
    },
    onMouseOut: () => {
      if (!openOnHover) {
        return;
      }

      setIsOpen(false);
    },
  };
  const contentProps = {
    onClose: (event) => {
      setIsOpen(false);

      if (typeof TargetContent?.props?.onClose === 'function') {
        TargetContent.props.onClose(event);
      }
    },
  };

  return {
    attributes,
    contentProps,
    isOpen,
    setArrowElement,
    setPopperElement,
    setReferenceElement,
    styles,
    Content: React.cloneElement(TargetContent, contentProps),
    Trigger: React.cloneElement(TargetTrigger, triggerProps),
  };
};

export default usePopOver;
